import { useRef, useState, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { DateTime } from 'luxon'

import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { WholeStoryDiscountTypeEnum } from '@interfaces/promotionCampaign/WholeStoryDiscountTypeEnum'
import { FreeReadingTypeEnum } from '@interfaces/promotionCampaign/FreeReadingTypeEnum'
import { CampaignFormType } from '@models/promotionCampaign/CampaignFormType'
import { CampaignBookItemType } from '@models/promotionCampaign/CampaignBookItemType'
import { splitCsvLine } from '@libs/csv'
import {
  UploadCampaignCsvFieldAcceptProps,
  UploadCampaignCsvFieldProps,
} from './interface'

function toStartDateString(value?: string): string | undefined {
  if (!value?.trim()) return undefined
  return DateTime.fromISO(value.trim()).startOf('day').toISO() || undefined
}

function toEndDateString(value?: string): string | undefined {
  if (!value?.trim()) return undefined
  return DateTime.fromISO(value.trim()).endOf('day').toISO() || undefined
}

// --- 6 Parser Functions ---

function parsePerChapterRow(cols: string[]): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    chapterIdStart: cols[4] ? Number(cols[4]) : undefined,
    chapterIdEnd: cols[5] ? Number(cols[5]) : undefined,
    discountPricePerChapter: cols[6] ? Number(cols[6]) : undefined,
    startDate: toStartDateString(cols[7]),
    endDate: toEndDateString(cols[8]),
  }
}

function parseWholeStoryPercentageRow(cols: string[]): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    discountPercentage: cols[4] ? Number(cols[4]) : undefined,
    startDate: toStartDateString(cols[5]),
    endDate: toEndDateString(cols[6]),
  }
}

function parseWholeStoryCoinPriceRow(cols: string[]): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    originalPrice: cols[4] ? Number(cols[4]) : undefined,
    discountCoin: cols[5] ? Number(cols[5]) : undefined,
    discountedPrice: cols[6] ? Number(cols[6]) : undefined,
    startDate: toStartDateString(cols[7]),
    endDate: toEndDateString(cols[8]),
  }
}

function parseFreeReadingDailyLimitRow(cols: string[]): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    dailyChapters: cols[4] ? Number(cols[4]) : undefined,
    startDate: toStartDateString(cols[5]),
    endDate: toEndDateString(cols[6]),
  }
}

function parseFreeReadingPeriodSpecificRow(
  cols: string[]
): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    freeDays: cols[4] ? Number(cols[4]) : undefined,
    startDate: toStartDateString(cols[5]),
    endDate: toEndDateString(cols[6]),
  }
}

function parseFreeReadingDurationSpecificRow(
  cols: string[]
): CampaignBookItemType {
  return {
    bookId: Number(cols[0]),
    bookTitle: cols[1]?.trim() || '',
    bookPenFirstName: cols[2]?.trim() || '',
    bookPublisherName: cols[3]?.trim() || '',
    startDate: toStartDateString(cols[4]),
    endDate: toEndDateString(cols[5]),
  }
}

// --- Date Validation Helper ---

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

function isValidDateString(value: string): boolean {
  if (!DATE_REGEX.test(value)) return false
  const dt = DateTime.fromISO(value)
  return dt.isValid
}

// --- Row-level Validation ---

function validateRows(
  rows: CampaignBookItemType[],
  rawLines: string[],
  campaignType: CampaignTypeEnum,
  wholeStoryDiscountType?: WholeStoryDiscountTypeEnum,
  freeReadingType?: FreeReadingTypeEnum
): string | null {
  const errors: string[] = []

  rows.forEach((row, index) => {
    const rowNum = index + 2 // +2 because line 1 is header, data starts at line 2
    const cols = splitCsvLine(rawLines[index])

    // bookId: ต้องเป็นตัวเลข > 0
    if (Number.isNaN(row.bookId) || row.bookId <= 0) {
      errors.push(`แถวที่ ${rowNum}: bookId ต้องเป็นตัวเลขมากกว่า 0`)
      return
    }

    // Required fields + numeric range by sub-type
    if (campaignType === CampaignTypeEnum.PER_CHAPTER) {
      if (!row.chapterIdStart || row.chapterIdStart <= 0) {
        errors.push(`แถวที่ ${rowNum}: ตั้งแต่ตอนที่ ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
      if (!row.chapterIdEnd || row.chapterIdEnd <= 0) {
        errors.push(`แถวที่ ${rowNum}: ถึงตอนที่ ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
      if (row.chapterIdStart > row.chapterIdEnd) {
        errors.push(
          `แถวที่ ${rowNum}: ตั้งแต่ตอนที่ ต้องน้อยกว่าหรือเท่ากับ ถึงตอนที่`
        )
        return
      }
      if (!row.discountPricePerChapter || row.discountPricePerChapter <= 0) {
        errors.push(`แถวที่ ${rowNum}: ราคาลดต่อตอน ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
    } else if (
      campaignType === CampaignTypeEnum.WHOLE_STORY &&
      wholeStoryDiscountType === WholeStoryDiscountTypeEnum.PERCENTAGE
    ) {
      if (!row.discountPercentage || row.discountPercentage <= 0) {
        errors.push(`แถวที่ ${rowNum}: ส่วนลด % ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
      if (row.discountPercentage > 100) {
        errors.push(`แถวที่ ${rowNum}: ส่วนลด % ต้องไม่เกิน 100`)
        return
      }
    } else if (
      campaignType === CampaignTypeEnum.WHOLE_STORY &&
      wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE
    ) {
      if (!row.originalPrice || row.originalPrice <= 0) {
        errors.push(`แถวที่ ${rowNum}: ราคาเต็ม ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
      if (!row.discountCoin || row.discountCoin <= 0) {
        errors.push(`แถวที่ ${rowNum}: ส่วนลดเหรียญ ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
      if (!row.discountedPrice || row.discountedPrice <= 0) {
        errors.push(`แถวที่ ${rowNum}: ราคาหลังลด ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
    } else if (
      campaignType === CampaignTypeEnum.FREE_READING &&
      freeReadingType === FreeReadingTypeEnum.DAILY_LIMIT
    ) {
      if (!row.dailyChapters || row.dailyChapters <= 0) {
        errors.push(`แถวที่ ${rowNum}: จำนวนตอนต่อวัน ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
    } else if (
      campaignType === CampaignTypeEnum.FREE_READING &&
      freeReadingType === FreeReadingTypeEnum.PERIOD_SPECIFIC
    ) {
      if (!row.freeDays || row.freeDays <= 0) {
        errors.push(`แถวที่ ${rowNum}: จำนวนวันฟรี ต้องเป็นตัวเลขมากกว่า 0`)
        return
      }
    }

    // Date format validation
    const startDateRaw =
      cols[
        campaignType === CampaignTypeEnum.PER_CHAPTER ||
        (campaignType === CampaignTypeEnum.WHOLE_STORY &&
          wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE)
          ? 7
          : campaignType === CampaignTypeEnum.FREE_READING &&
            freeReadingType === FreeReadingTypeEnum.DURATION_SPECIFIC
          ? 4
          : 5
      ]?.trim()

    const endDateRaw =
      cols[
        campaignType === CampaignTypeEnum.PER_CHAPTER ||
        (campaignType === CampaignTypeEnum.WHOLE_STORY &&
          wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE)
          ? 8
          : campaignType === CampaignTypeEnum.FREE_READING &&
            freeReadingType === FreeReadingTypeEnum.DURATION_SPECIFIC
          ? 5
          : 6
      ]?.trim()

    if (startDateRaw && !isValidDateString(startDateRaw)) {
      errors.push(
        `แถวที่ ${rowNum}: startDate รูปแบบวันที่ไม่ถูกต้อง (ต้องเป็น YYYY-MM-DD)`
      )
      return
    }
    if (endDateRaw && !isValidDateString(endDateRaw)) {
      errors.push(
        `แถวที่ ${rowNum}: endDate รูปแบบวันที่ไม่ถูกต้อง (ต้องเป็น YYYY-MM-DD)`
      )
      return
    }
    if (startDateRaw && endDateRaw) {
      const start = DateTime.fromISO(startDateRaw)
      const end = DateTime.fromISO(endDateRaw)
      if (start > end) {
        errors.push(`แถวที่ ${rowNum}: startDate ต้องไม่มากกว่า endDate`)
      }
    }
  })

  if (errors.length === 0) return null

  const maxDisplay = 5
  const displayed = errors.slice(0, maxDisplay)
  const remaining = errors.length - maxDisplay

  let message = displayed.join('\n')
  if (remaining > 0) {
    message += `\n...และอีก ${remaining} แถวที่มีข้อผิดพลาด`
  }

  return message
}

// --- Header Validation ---

function validateHeader(
  headerLine: string,
  campaignType: CampaignTypeEnum,
  wholeStoryDiscountType?: WholeStoryDiscountTypeEnum,
  freeReadingType?: FreeReadingTypeEnum
): string | null {
  const cols = splitCsvLine(headerLine)
  const colCount = cols.length
  const col4 = cols[4] || ''

  if (campaignType === CampaignTypeEnum.PER_CHAPTER) {
    if (colCount !== 9 || !col4.includes('ตอนที่')) {
      return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "โปรลดรายตอน"'
    }
    return null
  }

  if (campaignType === CampaignTypeEnum.WHOLE_STORY) {
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.PERCENTAGE) {
      if (colCount !== 7 || !col4.includes('%')) {
        return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "ลดแบบ %"'
      }
    } else if (
      wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE
    ) {
      if (colCount !== 9 || !col4.includes('ราคาเต็ม')) {
        return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "ลดแบบเหรียญ"'
      }
    } else {
      return 'กรุณาเลือกประเภทส่วนลดก่อนอัปโหลด CSV'
    }
    return null
  }

  // FREE_READING
  if (freeReadingType === FreeReadingTypeEnum.DAILY_LIMIT) {
    if (colCount !== 7 || !col4.includes('วันละ')) {
      return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "ฟรีวันละ x ตอน"'
    }
  } else if (freeReadingType === FreeReadingTypeEnum.DURATION_SPECIFIC) {
    if (colCount !== 6) {
      return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "ฟรีช่วงเวลา"'
    }
  } else if (freeReadingType === FreeReadingTypeEnum.PERIOD_SPECIFIC) {
    if (colCount !== 7 || !col4.includes('ภายใน')) {
      return 'ไฟล์ CSV ไม่ตรงกับประเภทที่เลือก กรุณาใช้ template สำหรับ "ฟรี x วัน"'
    }
  } else {
    return 'กรุณาเลือกประเภทอ่านฟรีก่อนอัปโหลด CSV'
  }

  return null
}

// --- Get Parser ---

function getParser(
  campaignType: CampaignTypeEnum,
  wholeStoryDiscountType?: WholeStoryDiscountTypeEnum,
  freeReadingType?: FreeReadingTypeEnum
): ((cols: string[]) => CampaignBookItemType) | null {
  if (campaignType === CampaignTypeEnum.PER_CHAPTER) {
    return parsePerChapterRow
  }

  if (campaignType === CampaignTypeEnum.WHOLE_STORY) {
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.PERCENTAGE) {
      return parseWholeStoryPercentageRow
    }
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE) {
      return parseWholeStoryCoinPriceRow
    }
    return null
  }

  // FREE_READING
  if (freeReadingType === FreeReadingTypeEnum.DAILY_LIMIT) {
    return parseFreeReadingDailyLimitRow
  }
  if (freeReadingType === FreeReadingTypeEnum.DURATION_SPECIFIC) {
    return parseFreeReadingDurationSpecificRow
  }
  if (freeReadingType === FreeReadingTypeEnum.PERIOD_SPECIFIC) {
    return parseFreeReadingPeriodSpecificRow
  }

  return null
}

export function withUploadCampaignCsvField(
  Component: React.FC<UploadCampaignCsvFieldProps>
) {
  function WithUploadCampaignCsvField({
    campaignType,
    wholeStoryDiscountType,
    freeReadingType,
    onErrorChange,
    ...props
  }: UploadCampaignCsvFieldAcceptProps) {
    const { values, setValues } = useFormikContext<CampaignFormType>()
    const inputRef = useRef<HTMLInputElement>(null)
    const [fileName, setFileName] = useState<string | undefined>()
    const [isError, setIsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    useEffect(() => {
      onErrorChange?.(isError)
    }, [isError, onErrorChange])

    useEffect(() => {
      setIsError(false)
      setErrorMessage(undefined)
      setFileName(undefined)
    }, [campaignType, wholeStoryDiscountType, freeReadingType])

    function parseCsvText(
      text: string
    ): CampaignBookItemType[] | { error: string } {
      const lines = text.split('\n').filter(line => line.trim())
      if (lines.length < 2) return { error: 'ไฟล์ CSV ไม่มีข้อมูล' }

      // Validate header
      const headerError = validateHeader(
        lines[0],
        campaignType,
        wholeStoryDiscountType,
        freeReadingType
      )
      if (headerError) return { error: headerError }

      const parser = getParser(
        campaignType,
        wholeStoryDiscountType,
        freeReadingType
      )
      if (!parser) {
        return { error: 'กรุณาเลือกประเภทย่อยก่อนอัปโหลด CSV' }
      }

      const dataLines = lines.slice(1) // skip header
      const parsed = dataLines.map(line => {
        const cols = splitCsvLine(line)
        return parser(cols)
      })

      // Row-level validation
      const rowError = validateRows(
        parsed,
        dataLines,
        campaignType,
        wholeStoryDiscountType,
        freeReadingType
      )
      if (rowError) return { error: rowError }

      return parsed
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      const { files } = event.target
      if (files && files[0]) {
        const file = files[0]
        setFileName(file.name)

        const reader = new FileReader()
        reader.onload = e => {
          try {
            const text = e.target?.result as string
            const result = parseCsvText(text)

            if ('error' in result) {
              setIsError(true)
              setErrorMessage(result.error)
              setValues({
                ...values,
                bookData: [],
                csvFile: undefined,
              })
            } else if (result.length > 0) {
              setIsError(false)
              setErrorMessage(undefined)
              setValues({
                ...values,
                bookData: result,
                csvFile: file,
              })
            } else {
              setIsError(true)
              setErrorMessage('ไฟล์ CSV ไม่มีข้อมูลที่ถูกต้อง')
              setValues({
                ...values,
                bookData: [],
                csvFile: undefined,
              })
            }
          } catch {
            setIsError(true)
            setErrorMessage('เกิดข้อผิดพลาดในการอ่านไฟล์ CSV')
            setValues({
              ...values,
              bookData: [],
              csvFile: undefined,
            })
          }
        }
        reader.readAsText(file)

        // eslint-disable-next-line no-param-reassign
        event.target.value = ''
      }
    }

    function showUploadDialog() {
      inputRef.current?.click()
    }

    function onClearSelectedFile() {
      setFileName(undefined)
      setIsError(false)
      setErrorMessage(undefined)
      setValues({
        ...values,
        bookData: [],
        csvFile: undefined,
      })
    }

    const newProps: UploadCampaignCsvFieldProps = {
      ...props,
      inputRef,
      fileName,
      isError,
      errorMessage,
      fileCount: values.bookData?.length,
      onChange: handleChange,
      showUploadDialog,
      onClearSelectedFile,
    }

    return <Component {...newProps} />
  }

  return WithUploadCampaignCsvField
}
